import React from 'react';
import { View, StyleSheet, Platform, useWindowDimensions } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { colors, gradients } from '../styles';
import { Typography, Card, Button } from './ui';
import { useTheme } from '../context/ThemeContext';
import { useI18n } from '../i18n';

interface FeatureCardProps {
  icon: keyof typeof Ionicons.glyphMap;
  title: string;
  subtitle: string;
  description: string;
  badge: string;
  action: string;
}

const FeatureCard: React.FC<FeatureCardProps> = ({ icon, title, subtitle, description, badge, action }) => {
  const { isDark, colors: themeColors } = useTheme();
  return (
  <View style={styles.cardWrapper}>
    <Card variant="glass" style={[styles.card, { backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(255, 255, 255, 0.6)' }] as any}>
      <View style={[styles.iconBox, isDark && { backgroundColor: 'rgba(237, 116, 61, 0.15)', borderColor: 'rgba(251, 146, 60, 0.3)' }]}>
        <Ionicons name={icon} size={28} color={colors.orange600} />
      </View>
      <Typography variant="overline" color={colors.orange600} style={styles.cardBadge}>{badge}</Typography>
      <Typography variant="h3" style={styles.cardTitle}>{title}</Typography>
      <Typography variant="h5" color={themeColors.textPrimary} style={styles.cardSubtitle}>{subtitle}</Typography>
      <Typography variant="bodySmall" color={themeColors.textSecondary} style={styles.cardDescription}>{description}</Typography>

      <View style={[styles.cardFooter, { borderTopColor: themeColors.border }]}>
        <Button variant="ghost" size="sm" style={styles.cardButton as any}>
          {action}
          <Ionicons name="arrow-forward" size={14} color={colors.orange700} style={{ marginLeft: 6 }} />
        </Button>
      </View>
    </Card>
  </View>
  );
};

export const TheSpace: React.FC = () => {
  const { isDark, colors: themeColors } = useTheme();
  const { t } = useI18n('home');
  const { width } = useWindowDimensions();
  const isLargeScreen = width >= 768;

  const features: FeatureCardProps[] = [
    {
      icon: 'videocam-outline',
      title: t('theSpace.liveStreams'),
      subtitle: t('theSpace.liveStreamsSubtitle'),
      description: t('theSpace.liveStreamsDesc'),
      badge: t('theSpace.liveStreamsBadge'),
      action: t('theSpace.liveStreamsAction'),
    },
    {
      icon: 'sparkles-outline',
      title: t('theSpace.aiCompanion'),
      subtitle: t('theSpace.aiCompanionSubtitle'),
      description: t('theSpace.aiCompanionDesc'),
      badge: t('theSpace.aiCompanionBadge'),
      action: t('theSpace.aiCompanionAction'),
    },
    {
      icon: 'heart-outline',
      title: t('theSpace.agapePractices'),
      subtitle: t('theSpace.agapePracticesSubtitle'),
      description: t('theSpace.agapePracticesDesc'),
      badge: t('theSpace.agapePracticesBadge'),
      action: t('theSpace.agapePracticesAction'),
    },
    {
      icon: 'people-outline',
      title: t('theSpace.communities'),
      subtitle: t('theSpace.communitiesSubtitle'),
      description: t('theSpace.communitiesDesc'),
      badge: t('theSpace.communitiesBadge'),
      action: t('theSpace.communitiesAction'),
    },
  ];

  return (
    <View style={[styles.section, { backgroundColor: themeColors.canvas }]}>
      <View style={styles.container}>
        <View style={styles.header}>
          <View style={styles.breadcrumb}>
            <Typography variant="overline" color={colors.orange600}>{t('theSpace.overline')}</Typography>
          </View>
          <Typography variant={Platform.OS === 'web' ? 'displayH1' : 'h1'} style={styles.title}>{t('theSpace.title')}</Typography>
          <Card variant="glass" style={[styles.descriptionBox, { backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(255, 255, 255, 0.4)' }] as any}>
            <Typography variant="h4" color={themeColors.textPrimary} style={styles.descriptionText}>
              {t('theSpace.description')}
            </Typography>
          </Card>
        </View>

        <View style={styles.grid}>
          {features.map((feature, index) => (
            <FeatureCard key={index} {...feature} />
          ))}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  section: {
    paddingVertical: 128,
  },
  container: {
    maxWidth: 1280,
    marginHorizontal: 'auto',
    paddingHorizontal: 24,
    width: '100%',
  },
  header: {
    alignItems: 'center',
    marginBottom: 80,
  },
  breadcrumb: {
    marginBottom: 16,
  },
  title: {
    marginBottom: 32,
    textAlign: 'center',
  },
  descriptionBox: {
    padding: 32,
    borderRadius: 24,
    maxWidth: 768,
    backgroundColor: 'rgba(255, 255, 255, 0.4)',
  },
  descriptionText: {
    fontWeight: '400',
    textAlign: 'center',
    lineHeight: 32,
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginHorizontal: -12,
  },
  cardWrapper: {
    width: Platform.OS === 'web' ? '50%' : '100%',
    padding: 12,
    minWidth: 320,
  } as any,
  card: {
    padding: 40,
    height: '100%',
    backgroundColor: 'rgba(255, 255, 255, 0.6)',
  },
  iconBox: {
    width: 64,
    height: 64,
    borderRadius: 20,
    backgroundColor: colors.orange50,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 32,
    borderWidth: 1,
    borderColor: 'rgba(251, 146, 60, 0.2)',
  },
  cardBadge: {
    marginBottom: 12,
  },
  cardTitle: {
    marginBottom: 12,
  },
  cardSubtitle: {
    marginBottom: 16,
  },
  cardDescription: {
    lineHeight: 22,
    marginBottom: 32,
  },
  cardFooter: {
    marginTop: 'auto',
    borderTopWidth: 1,
    borderTopColor: colors.slate100,
    paddingTop: 24,
    flexDirection: 'row',
    alignItems: 'center',
  },
  cardButton: {
    paddingLeft: 0,
  },
});
